import { createContext, useState, useEffect, useContext, useCallback, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { checkFormAssemblyConnection } from "@/services/formassemblyService";
import { checkJotformConnection } from "@/services/jotformService";
import { logger } from "@/lib/logger";

export type ActiveProvider = 'formassembly' | 'jotform';

interface ProviderContextType {
  connectionStatus: Record<ActiveProvider, boolean | null>;
  isCheckingConnections: boolean;
  activeProvider: ActiveProvider;
  setActiveProvider: (provider: ActiveProvider) => void;
  isActiveProviderConnected: boolean | null;
  connectedProviders: ActiveProvider[];
  refreshConnectionStatus: (provider?: ActiveProvider) => Promise<void>;
}

const ProviderContext = createContext<ProviderContextType | undefined>(undefined);

const ACTIVE_PROVIDER_KEY = 'fab_activeProvider';

export const ProviderProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  const [connectionStatus, setConnectionStatus] = useState<Record<ActiveProvider, boolean | null>>({
    formassembly: null,
    jotform: null,
  });
  const [isCheckingConnections, setIsCheckingConnections] = useState(false);

  const [activeProvider, setActiveProviderState] = useState<ActiveProvider>(() => {
    try {
      const saved = localStorage.getItem(ACTIVE_PROVIDER_KEY);
      if (saved === 'formassembly' || saved === 'jotform') return saved;
    } catch {
      // localStorage not available
    }
    return 'formassembly';
  });

  const setActiveProvider = useCallback((provider: ActiveProvider) => {
    setActiveProviderState(provider);
    try {
      localStorage.setItem(ACTIVE_PROVIDER_KEY, provider);
    } catch {
      // localStorage not available
    }
  }, []);

  const refreshConnectionStatus = useCallback(async (provider?: ActiveProvider) => {
    if (!user) return;

    if (provider) {
      const checker = provider === 'formassembly' ? checkFormAssemblyConnection : checkJotformConnection;
      try {
        const result = await checker();
        setConnectionStatus(prev => ({ ...prev, [provider]: result }));
      } catch (err) {
        logger.error(`Error checking ${provider} connection:`, err);
      }
      return;
    }

    // Check all providers
    setIsCheckingConnections(true);
    try {
      const [fa, jf] = await Promise.all([
        checkFormAssemblyConnection().catch(err => {
          logger.error("Error checking FormAssembly connection:", err);
          return false;
        }),
        checkJotformConnection().catch(err => {
          logger.error("Error checking Jotform connection:", err);
          return false;
        }),
      ]);
      setConnectionStatus({ formassembly: fa, jotform: jf });
    } finally {
      setIsCheckingConnections(false);
    }
  }, [user]);

  // Check connections on mount when authenticated
  useEffect(() => {
    if (user) {
      refreshConnectionStatus();
    } else {
      setConnectionStatus({ formassembly: null, jotform: null });
    }
  }, [user, refreshConnectionStatus]);

  const connectedProviders = (Object.entries(connectionStatus) as [ActiveProvider, boolean | null][])
    .filter(([, status]) => status === true)
    .map(([provider]) => provider);

  const value: ProviderContextType = {
    connectionStatus,
    isCheckingConnections,
    activeProvider,
    setActiveProvider,
    isActiveProviderConnected: connectionStatus[activeProvider],
    connectedProviders,
    refreshConnectionStatus,
  };

  return (
    <ProviderContext.Provider value={value}>
      {children}
    </ProviderContext.Provider>
  );
};

export const useProvider = (): ProviderContextType => {
  const context = useContext(ProviderContext);
  if (!context) {
    throw new Error("useProvider must be used within a ProviderProvider");
  }
  return context;
};
